import React, { useState } from "react";
import api from "../api";

const FIELDS = ["customer_ref", "customer_name", "mobile", "email", "branch", "purpose_id", "status", "channel", "consent_date", "expiry_date"];
const TEMPLATE = `${FIELDS.join(",")}\nCIF1001,Ramesh Kumar,9876543210,ramesh@example.com,Main Branch,1,Active,Branch,2026-01-15,`;

// Minimal RFC-4180-ish CSV parser (handles quotes + embedded commas/newlines).
function parseCsv(text) {
  const rows = []; let field = "", row = [], inQ = false, i = 0;
  const pushF = () => { row.push(field); field = ""; };
  const pushR = () => { rows.push(row); row = []; };
  while (i < text.length) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } inQ = false; i++; continue; }
      field += ch; i++; continue;
    }
    if (ch === '"') { inQ = true; i++; continue; }
    if (ch === ",") { pushF(); i++; continue; }
    if (ch === "\r") { i++; continue; }
    if (ch === "\n") { pushF(); pushR(); i++; continue; }
    field += ch; i++;
  }
  if (field.length || row.length) { pushF(); pushR(); }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
}

export default function BulkConsent() {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);      // { headers, rows }
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result)); doParse(String(reader.result)); };
    reader.readAsText(f);
  };

  const doParse = (raw) => {
    setErr(""); setResult(null);
    const grid = parseCsv(raw || text);
    if (grid.length < 2) { setErr("Need a header row + at least one data row."); setParsed(null); return; }
    const headers = grid[0].map((h) => h.trim().toLowerCase());
    const rows = grid.slice(1).map((r) => {
      const o = {}; headers.forEach((h, idx) => { if (FIELDS.includes(h)) o[h] = (r[idx] ?? "").trim(); });
      return o;
    });
    setParsed({ headers, rows });
  };

  const doImport = async () => {
    if (!parsed?.rows?.length) return;
    setBusy(true); setErr(""); setResult(null);
    try { const r = await api.post("/consent/bulk", { rows: parsed.rows }); setResult(r.data?.data); }
    catch (e) { setErr(e?.response?.data?.message || "Import failed"); }
    setBusy(false);
  };

  const unknown = parsed ? parsed.headers.filter((h) => !FIELDS.includes(h)) : [];

  return (
    <div>
      <h1>Bulk Consent Import</h1>
      <p className="sub">Import existing customer consents from a CSV — useful during migration or campaigns.</p>

      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input type="file" accept=".csv,text/csv" onChange={onFile} />
          <span style={{ color: "#64748b" }}>or paste CSV below</span>
          <div style={{ flex: 1 }} />
          <button className="btn sm" onClick={() => { setText(TEMPLATE); doParse(TEMPLATE); }}>Load template</button>
        </div>
        <textarea style={{ marginTop: 10, minHeight: 120, fontFamily: "monospace" }} value={text} onChange={(e) => setText(e.target.value)} placeholder={TEMPLATE} />
        <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center" }}>
          <button className="btn" onClick={() => doParse()}>Parse / Preview</button>
          <button className="btn primary" disabled={busy || !parsed?.rows?.length} onClick={doImport}>{busy ? "Importing…" : `Import ${parsed?.rows?.length || 0} row(s)`}</button>
          <div style={{ color: "#64748b", fontSize: 12 }}>Recognised columns: {FIELDS.join(", ")}</div>
        </div>
        {err && <div className="err">{err}</div>}
        {unknown.length > 0 && <div style={{ color: "#d97706", fontSize: 13, marginTop: 6 }}>Ignored unknown columns: {unknown.join(", ")}</div>}
      </div>

      {result && (
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <b>Import result</b>
          <div style={{ marginTop: 6 }}>
            <span className="pill ok">Imported {result.inserted}</span>{" "}
            {result.skipped > 0 && <span className="pill warn">Skipped {result.skipped}</span>}
          </div>
          {result.errors?.length > 0 && (
            <ul style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
              {result.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}

      {parsed?.rows?.length > 0 && !result && (
        <div className="card">
          <div className="toolbar"><b>Preview</b><span style={{ color: "#64748b", fontSize: 13 }}>{parsed.rows.length} rows</span></div>
          <div className="tablewrap">
            <table>
              <thead><tr>{FIELDS.map((f) => <th key={f}>{f}</th>)}</tr></thead>
              <tbody>
                {parsed.rows.slice(0, 25).map((r, i) => (
                  <tr key={i}>{FIELDS.map((f) => <td key={f}>{r[f] || <span style={{ color: "#cbd5e1" }}>—</span>}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
